<template>
	<n-modal v-model:show="show" :mask-closable="false">
		<div class="candidate-tags-dialog" @click.stop>
			<button class="close-btn" type="button" aria-label="close" @click="handleClose">
				<span />
				<span />
			</button>

			<h2 class="dialog-title">{{ title }}</h2>
			<p class="dialog-subtitle">{{ subtitle }}</p>

			<div v-if="loading" class="dialog-status">加載中...</div>
			<div v-else-if="displayOptions.length" class="tag-list">
				<button
					v-for="tag in displayOptions"
					:key="tag"
					class="tag-item"
					:class="{ active: selectedTags.includes(tag) }"
					type="button"
					@click="toggleTag(tag)"
				>
					{{ tag }}
				</button>
			</div>
			<div v-else class="dialog-status">暫無可選內容</div>

			<button
				class="confirm-btn"
				:class="{ disabled: !selectedTags.length || submitting }"
				:type="'button'"
				:disabled="!selectedTags.length || submitting"
				@click="handleConfirm"
			>
				{{ confirmButtonText }}
			</button>
		</div>
	</n-modal>
</template>

<script setup>
import { computed, onBeforeUnmount, onMounted, ref, watch } from "vue";
import {
	getColdStartCandidateTags,
	getColdStartUserSelectedTags,
	getUserInfo,
	trackEvents,
} from "~/api/api";
import {
	buildColdStartTagsTrackPayload,
	clearColdStartTagsPending,
	COLD_START_TAGS_CONFIRMED_EVENT,
	consumeColdStartTagsAutoTrigger,
	COLD_START_TAGS_TAB_SWITCH_EVENT,
	hasShownColdStartTags,
	markColdStartTagsShown,
} from "~/utils/coldStartTags";
import eventBus from "~/utils/eventBus";

const FALLBACK_OPTIONS = [
	"塔羅",
	"星座",
	"八字",
	"紫微斗數",
	"手相",
	"相學",
	"奇門遁甲",
	"生肖",
	"風水",
	"神秘事件",
	"心理",
	"冥想",
	"心靈療愈",
	"靈性成長",
	"事業",
	"家庭",
	"健康",
	"婚姻",
	"感情",
	"財運",
	"祈福",
	"驅邪",
];

const props = defineProps({
	title: {
		type: String,
		default: "比較喜歡什麼內容?",
	},
	subtitle: {
		type: String,
		default: "選擇感興趣的主題，幫你找到喜歡的內容",
	},
	confirmText: {
		type: String,
		default: "完成",
	},
	maxSelect: {
		type: Number,
		default: 0,
	},
	options: {
		type: Array,
		default: () => [],
	},
	initialSelectedTags: {
		type: Array,
		default: () => [],
	},
	fetchOnOpen: {
		type: Boolean,
		default: true,
	},
	closeOnConfirm: {
		type: Boolean,
		default: true,
	},
	autoTriggerOnFirstTabSwitch: {
		type: Boolean,
		default: false,
	},
});

const emit = defineEmits(["confirm", "close", "change", "loaded"]);
const show = defineModel({
	type: Boolean,
	default: false,
});

const loading = ref(false);
const submitting = ref(false);
const remoteOptions = ref([]);
const selectedTags = ref([]);

const isMobileViewport = () => {
	if (!import.meta.client || typeof window === "undefined") return false;
	return window.innerWidth < 768;
};

const normalizeOptions = list => {
	if (!Array.isArray(list)) return [];

	return [...new Set(list.map(item => String(item || "").trim()).filter(Boolean))];
};

const displayOptions = computed(() => {
	if (remoteOptions.value.length) {
		return remoteOptions.value;
	}

	if (props.options.length) {
		return normalizeOptions(props.options);
	}

	return FALLBACK_OPTIONS;
});

const confirmButtonText = computed(
	() => `${props.confirmText}（已選${selectedTags.value.length}個）`
);

const resetSelection = () => {
	const normalizedSelectedTags = normalizeOptions(props.initialSelectedTags);

	selectedTags.value =
		props.maxSelect > 0 ? normalizedSelectedTags.slice(0, props.maxSelect) : normalizedSelectedTags;
};

const fetchOptions = async () => {
	if (!props.fetchOnOpen) return;

	loading.value = true;
	try {
		const res = await getColdStartCandidateTags();
		const remoteTags = normalizeOptions(res?.data?.cold_start_candidate_tags);

		if (remoteTags.length) {
			remoteOptions.value = remoteTags;
			emit("loaded", remoteTags);
		}
	} catch (error) {
		console.error("獲取冷啟動候選標籤失敗:", error);
	} finally {
		loading.value = false;
	}
};

const toggleTag = tag => {
	const nextSelected = [...selectedTags.value];
	const activeIndex = nextSelected.indexOf(tag);

	if (activeIndex > -1) {
		nextSelected.splice(activeIndex, 1);
		selectedTags.value = nextSelected;
		emit("change", nextSelected);
		return;
	}

	nextSelected.push(tag);
	selectedTags.value = nextSelected;
	emit("change", nextSelected);
};

const handleClose = () => {
	show.value = false;
};

const resolveUserId = async () => {
	try {
		const res = await getUserInfo();
		return res?.data?.user_id || res?.data?.uid || res?.data?.id || res?.data?.socket_uid || "";
	} catch (error) {
		console.error("獲取當前用戶 ID 失敗:", error);
		return "";
	}
};

const shouldShowDialogByUserSelectedTags = async () => {
	const userId = await resolveUserId();
	if (!userId) return false;

	try {
		const res = await getColdStartUserSelectedTags(userId);
		const userSelectedTags = res?.data?.user_selected_tags;
		return Array.isArray(userSelectedTags) && userSelectedTags.length === 0;
	} catch (error) {
		console.error("獲取冷啟動已選標籤失敗:", error);
		return false;
	}
};

const handleAutoTrigger = async () => {
	if (!props.autoTriggerOnFirstTabSwitch || show.value) return;
	if (isMobileViewport()) return;
	if (hasShownColdStartTags()) return;
	if (!(await shouldShowDialogByUserSelectedTags())) return;

	markColdStartTagsShown();
	show.value = true;
};

const handleAutoTriggerEvent = async () => {
	consumeColdStartTagsAutoTrigger();
	await handleAutoTrigger();
};

const handleConfirm = async () => {
	if (!selectedTags.value.length) return;

	submitting.value = true;

	try {
		const userId = await resolveUserId();
		const payload = buildColdStartTagsTrackPayload(selectedTags.value, userId);

		await trackEvents(payload);
		clearColdStartTagsPending();
		eventBus.emit(COLD_START_TAGS_CONFIRMED_EVENT);
		emit("confirm", [...selectedTags.value]);

		if (props.closeOnConfirm) {
			show.value = false;
		}
	} catch (error) {
		console.error("提交冷啟動標籤失敗:", error);
	} finally {
		submitting.value = false;
	}
};

onMounted(() => {
	if (props.autoTriggerOnFirstTabSwitch) {
		eventBus.on(COLD_START_TAGS_TAB_SWITCH_EVENT, handleAutoTriggerEvent);
		if (consumeColdStartTagsAutoTrigger()) {
			void handleAutoTrigger();
		}
	}
});

onBeforeUnmount(() => {
	if (props.autoTriggerOnFirstTabSwitch) {
		eventBus.off(COLD_START_TAGS_TAB_SWITCH_EVENT, handleAutoTriggerEvent);
	}
});

watch(
	() => show.value,
	async visible => {
		if (visible) {
			if (isMobileViewport()) {
				show.value = false;
				return;
			}
			resetSelection();
			await fetchOptions();
			return;
		}

		emit("close");
	}
);

watch(
	() => props.initialSelectedTags,
	() => {
		if (show.value) {
			resetSelection();
		}
	},
	{ deep: true }
);
</script>

<style lang="scss" scoped>
.candidate-tags-dialog {
	position: relative;
	width: 700px;
	padding: 48px 52px 40px;
	background: #ffffff;
	border-radius: 20px;
	box-shadow: 0 12px 40px rgba(0, 0, 0, 0.14);
	box-sizing: border-box;
}

.close-btn {
	position: absolute;
	top: 22px;
	right: 22px;
	width: 30px;
	height: 30px;
	padding: 0;
	background: transparent;
	cursor: pointer;
	border: none;

	span {
		position: absolute;
		top: 14px;
		left: 6px;
		width: 16px;
		height: 1px;
		background: #555555;

		&:first-child {
			transform: rotate(45deg);
		}

		&:last-child {
			transform: rotate(-45deg);
		}
	}
}

.dialog-title {
	margin: 0;
	font-size: 24px;
	font-weight: 700;
	line-height: 1.4;
	color: #303030;
}

.dialog-subtitle {
	margin: 10px 0 0;
	font-size: 16px;
	line-height: 1.5;
	color: #777777;
}

.dialog-status {
	padding: 60px 0 40px;
	font-size: 16px;
	line-height: 1.5;
	text-align: center;
	color: #999999;
}

.tag-list {
	display: grid;
	grid-template-columns: repeat(4, minmax(0, 1fr));
	gap: 22px 22px;
	margin-top: 34px;
}

.tag-item {
	display: flex;
	align-items: center;
	justify-content: center;
	min-height: 44px;
	padding: 10px 12px;
	background: #ffffff;
	border: 1px solid #e3e3e3;
	border-radius: 12px;
	font-size: 16px;
	font-weight: 500;
	line-height: 1.4;
	color: #333333;
	cursor: pointer;
	transition:
		border-color 0.2s ease,
		box-shadow 0.2s ease;

	&:hover {
		border-color: #888888;
	}

	&.active {
		background: #fef7e8;
		border-color: #f2af19;
		box-shadow: inset 0 0 0 1px #f2af19;
		color: #f2af19;
	}
}

.confirm-btn {
	display: block;
	width: 404px;
	height: 50px;
	margin: 30px auto 0;
	background: #333333;
	border: none;
	border-radius: 12px;
	font-size: 18px;
	font-weight: 500;
	color: #ffffff;
	cursor: pointer;

	&.disabled {
		background: #cfcfcf;
		cursor: not-allowed;
	}
}
</style>
